import React, { useState } from 'react';
import { ValidationResults as ValidationResultsType, FileSpec, AIInsight, CommonIssue } from '../types';
import { AIInsights } from './AIInsights';
import { ErrorList } from './ErrorList';
import { DataPreview } from './DataPreview';
import { SpecReference } from './SpecReference';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';

interface ValidationResultsProps {
  results: ValidationResultsType;
  spec: FileSpec;
  insights: AIInsight[];
  commonIssues: CommonIssue[];
  healthScore: number;
  onExport: () => void;
  onCopyErrors: () => void;
}

type TabType = 'errors' | 'preview' | 'spec';

function buildRows(errors: ValidationResultsType['errors'], warnings: ValidationResultsType['warnings']) {
  return [
    ...errors.map(e => ({ ...e, severity: 'error' as const })),
    ...warnings.map(w => ({ ...w, severity: 'warning' as const })),
  ];
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportAsJSON(errors: ValidationResultsType['errors'], warnings: ValidationResultsType['warnings']) {
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  downloadFile(JSON.stringify(buildRows(errors, warnings), null, 2), `errors-warnings-${ts}.json`, 'application/json');
}

function exportAsCSV(errors: ValidationResultsType['errors'], warnings: ValidationResultsType['warnings']) {
  const headers = ['severity', 'line', 'field', 'message', 'expected', 'actual'];
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = buildRows(errors, warnings);
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => esc(r[h as keyof typeof r])).join(','))].join('\n');
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  downloadFile(csv, `errors-warnings-${ts}.csv`, 'text/csv');
}

export const ValidationResults: React.FC<ValidationResultsProps> = ({
  results,
  spec,
  insights,
  commonIssues,
  healthScore,
  onExport,
  onCopyErrors
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('errors');

  const errorCount = results.errors.filter(e => e.severity === 'error').length;
  const warningCount = results.warnings.length;

  return (
    <Card>
      <Grid columns={4} space={16} style={{ marginBottom: '16px' }}>
        <Card>
          <Text fontSize="lg" fontWeight="bold">Total Records: {results.totalRecords}</Text>
        </Card>

        <Card>
          <Text fontSize="lg" fontWeight="bold">Valid Records: {results.validRecords}</Text>
        </Card>

        <Card>
          <Text fontSize="lg" fontWeight="bold">Errors: {errorCount}</Text>
        </Card>

        <Card>
          <Text fontSize="lg" fontWeight="bold">Warnings: {warningCount}</Text>
        </Card>
      </Grid>

      <AIInsights
        results={results}
        insights={insights}
        commonIssues={commonIssues}
        healthScore={healthScore}
      />

      <Flex style={{ marginTop: 16, marginBottom: 16 }}>
        <Button variant={activeTab === 'errors' ? 'filled' : 'text'} onClick={() => setActiveTab('errors')}>
          Errors & Warnings
        </Button>
        <Button variant={activeTab === 'preview' ? 'filled' : 'text'} onClick={() => setActiveTab('preview')}>
          Data Preview
        </Button>
        <Button variant={activeTab === 'spec' ? 'filled' : 'text'} onClick={() => setActiveTab('spec')}>
          Specification Reference
        </Button>
      </Flex>

      <div>
        {activeTab === 'errors' && <ErrorList errors={[
          ...results.errors,
          ...results.warnings.map(w => ({ ...w, severity: 'warning' as const })),
        ]} />}
        {activeTab === 'preview' && <DataPreview records={results.records} />}
        {activeTab === 'spec' && <SpecReference spec={spec} />}
      </div>

      {activeTab === 'errors' && (
        <Flex justify="flex-end" style={{ marginTop: 20 }}>
          <Button variant="filled" onClick={() => exportAsJSON(results.errors, results.warnings)}>
            Export as JSON
          </Button>
          <Button variant="filled" onClick={() => exportAsCSV(results.errors, results.warnings)}>
            Export as CSV
          </Button>
          <Button variant="outline" onClick={onCopyErrors}>
            Copy Errors to Clipboard
          </Button>
        </Flex>
      )}
    </Card>
  );
};
